/**
 * Object to Object Plan service — backed by AI Hub
 * `GET/POST /assessment-plans`, `GET/PATCH/DELETE /assessment-plans/{id}`,
 * `GET /assessment-plans/dimensions`,
 * `GET /assessment-plans/configurable-concepts`,
 * `GET /assessment-plans/active`,
 * `GET /assessment-plans/default-prompt`.
 * Assessment Name options share `GET /plans/names`.
 *
 * Object type options come from `configurable-concepts` (not a separate
 * `/assessment-plans/types` endpoint). Write/read no longer use object
 * `sub_type` — filters live in `reference_items` / `impacted_items`;
 * Impact=Y write-back uses `assessment_record_items`; Match Condition
 * `weight` is a decimal.
 *
 * @see https://cmomtp3k98.execute-api.us-east-1.amazonaws.com/aihub/docs#/
 */

import { apiClient } from "./apiClient";
import {
  formatConceptCodeToLabel,
  invalidateAutoTaggingPlanNamesCache,
  normalizeAutoTaggingPlanDimensionItem,
  normalizeAutoTaggingPlanNameQuery,
  normalizeConfigurableConcept,
  toAutoTaggingPlanDimensionItemWrite,
  type AutoTaggingPlanDimensionItem,
  type AutoTaggingPlanDimensionItemWrite,
  type AutoTaggingPlanTypeOption,
  type ConfigurableConcept,
} from "./autoTaggingService";

export const OTO_PLANS_DEFAULT_PAGE_SIZE = 10;

/** Match Condition weight — API decimal in [0, 1], at most one decimal place. */
export type OtoWeight = number;

export type OtoMatchCondition = {
  dimensionCode: string;
  dimensionLabel: string;
  weight: OtoWeight;
};

/**
 * Whether `raw` is allowed in the Weight text field while typing.
 * Blocks values outside 0–1 and more than one digit after the decimal.
 * Allows intermediate forms like `""`, `"0."`, `"1."`, `"."`.
 */
export function isAllowedOtoWeightInput(raw: string): boolean {
  if (raw === "") return true;
  if (!/^\d*\.?\d*$/.test(raw)) return false;
  const dotIndex = raw.indexOf(".");
  if (dotIndex !== -1 && raw.slice(dotIndex + 1).length > 1) return false;
  // Disallow leading zeros like "00" / "01" (keep "0" and "0.x").
  if (/^0\d/.test(raw)) return false;
  if (raw === "." || raw.endsWith(".")) {
    const head = raw.slice(0, -1);
    if (head === "") return true;
    const headNum = Number(head);
    return Number.isFinite(headNum) && headNum >= 0 && headNum <= 1;
  }
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= 1;
}

/** Format a stored weight for the Match Conditions Weight field. */
export function formatOtoWeightForInput(weight: number): string {
  if (!Number.isFinite(weight)) return "";
  const clamped = Math.min(1, Math.max(0, weight));
  const rounded = Math.round(clamped * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Parse / validate Weight field text for submit.
 * @returns error message, or `undefined` when valid.
 */
export function otoWeightInputError(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === ".") {
    return "Please enter a weight.";
  }
  if (!isAllowedOtoWeightInput(trimmed)) {
    return "Weight must be between 0 and 1 with at most one decimal place.";
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    return "Weight must be between 0 and 1.";
  }
  const decimalPart = trimmed.includes(".")
    ? trimmed.split(".")[1] ?? ""
    : "";
  if (decimalPart.length > 1) {
    return "Weight must have at most one decimal place.";
  }
  return undefined;
}

/** Parse a validated Weight field string to a number (defaults to 1). */
export function parseOtoWeightInput(raw: string): OtoWeight {
  if (otoWeightInputError(raw)) return 1;
  const value = Number(raw.trim());
  return Number.isFinite(value) ? value : 1;
}

export type OtoPlan = {
  id: string;
  /** Assessment Name — API `name`. */
  name: string;
  /** Reference object type — API `reference_object.type` / `reference_object_type`. */
  objectAType: string;
  /**
   * Derived UI cascade state from `reference_items` (legacy `sub_type` fallback).
   * Not written back as object sub_type.
   */
  objectASubType: string;
  objectATypeName: string;
  objectASubTypeName: string;
  /** Impacted object type — API `impacted_object.type` / `impacted_object_type`. */
  objectBType: string;
  objectBSubType: string;
  objectBTypeName: string;
  objectBSubTypeName: string;
  /** API `reference_items`. */
  referenceItems: AutoTaggingPlanDimensionItem[];
  /** API `impacted_items`. */
  impactedItems: AutoTaggingPlanDimensionItem[];
  matchingDimensions: OtoMatchCondition[];
  /** Assessment Template — API `assessment_type`. */
  assessmentType: string;
  /** Assessment Prompt — API `assessment_prompt`. */
  prompt: string;
  /** API `assessment_record_items`. */
  assessmentRecordItems: AutoTaggingPlanDimensionItem[];
  /**
   * Fields to update on the assessment record when Impact = Y.
   * Derived from `assessment_record_items` (legacy `update_assessment_record`).
   */
  updateAssessmentRecord: string[];
  /** Dimension concept code for Update Assessment Record (from items). */
  updateAssessmentRecordConcept: string;
  /** API `enable_assessment` (default false). */
  enableAssessment: boolean;
  /**
   * Read-only from API — never include in POST/PATCH.
   * API `assessment_ready`.
   */
  assessmentReady: boolean;
  /**
   * Read-only from API — never include in POST/PATCH.
   * API `assessment_ready_reason`.
   */
  assessmentReadyReason: string;
  active: boolean;
};

export type OtoPlanListQuery = {
  /** Filter key `type|subtype` (lowercase), empty = all. */
  objectA?: string;
  objectB?: string;
  /** Top-bar search → API `keyword` (fuzzy Assessment Name). */
  keyword?: string;
  /** Assessment Name filter → API `name` (exact). */
  name?: string;
  active?: boolean;
  page?: number;
  pageSize?: number;
};

export type OtoPlanListResult = {
  items: OtoPlan[];
  total: number;
  page: number;
  pageSize: number;
};

export type OtoPlanUpsertPayload = {
  /** Assessment Name — required on create; optional on patch. */
  name: string;
  objectAType: string;
  objectATypeName: string;
  objectBType: string;
  objectBTypeName: string;
  /**
   * Kept for UI cascade / fingerprints. Not sent as object `sub_type` —
   * filters go through `referenceItems` / `impactedItems`.
   */
  objectASubType?: string;
  objectASubTypeName?: string;
  objectBSubType?: string;
  objectBSubTypeName?: string;
  /** API `reference_items`. Defaults to `[]`. */
  referenceItems?: AutoTaggingPlanDimensionItemWrite[];
  /** API `impacted_items`. Defaults to `[]`. */
  impactedItems?: AutoTaggingPlanDimensionItemWrite[];
  matchingDimensions: OtoMatchCondition[];
  /** Assessment Template — API `assessment_type`. */
  assessmentType?: string;
  /** API `assessment_record_items`. Defaults to `[]`. */
  assessmentRecordItems?: AutoTaggingPlanDimensionItemWrite[];
  /**
   * Assessment Prompt — API `assessment_prompt`.
   * Empty → null; PATCH `null` clears the stored prompt.
   */
  prompt: string;
  /** API `enable_assessment` (POST default false). */
  enableAssessment?: boolean;
  active?: boolean;
};

export type OtoDimensionAvailability = "both" | "a_only" | "b_only";

export type OtoDimensionOption = {
  code: string;
  name: string;
  /**
   * Whether the dimension exists on both object sides.
   * One-sided dimensions may be selected but can never match (AC3 warning).
   */
  availability: OtoDimensionAvailability;
};

export const OTO_PLAN_CREATE_SUCCESS_TOAST =
  "Object to object plan added successfully.";

export const OTO_PLAN_EDIT_SUCCESS_TOAST =
  "Object to object plan edited successfully.";

export const OTO_ONE_SIDED_DIMENSION_WARNING =
  "This dimension exists on only one object side and can never match.";

export function otoDimensionAvailabilityLabel(
  availability: OtoDimensionAvailability,
): string | null {
  if (availability === "a_only") return "Object A only";
  if (availability === "b_only") return "Object B only";
  return null;
}

export function formatOtoObjectLabel(
  typeName: string,
  subTypeName: string,
): string {
  const type = typeName.trim();
  const sub = subTypeName.trim();
  if (type && sub) return `${type} - ${sub}`;
  return type || sub || "";
}

export function formatOtoMatchingDimensions(
  conditions: readonly OtoMatchCondition[],
): string {
  return conditions
    .map((item) => `${item.dimensionLabel}=${item.weight}`)
    .join(", ");
}

/** Split filter value `type|subtype` into API query parts. */
export function parseOtoObjectFilterKey(filter: string | undefined): {
  type?: string;
  subType?: string;
} {
  const trimmed = filter?.trim();
  if (!trimmed) return {};
  const sep = trimmed.indexOf("|");
  if (sep < 0) {
    return { type: trimmed };
  }
  return {
    type: trimmed.slice(0, sep).trim() || undefined,
    subType: trimmed.slice(sep + 1).trim(),
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function readBoolean(
  record: Record<string, unknown>,
  ...keys: string[]
): boolean | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
  }
  return undefined;
}

function readNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function displayNameForCode(code: string, explicit?: string): string {
  const named = explicit?.trim();
  if (named) return named;
  return formatConceptCodeToLabel(code) || code;
}

/**
 * Read dimension identity + display label from either a plain string or
 * `{ name, display_name }` (also accepts displayName / displayname).
 */
function readDimensionRef(value: unknown): { code: string; label: string } {
  if (typeof value === "string") {
    const code = value.trim();
    return { code, label: "" };
  }
  const record = asRecord(value);
  if (!record) return { code: "", label: "" };

  const code = readString(
    record,
    "name",
    "code",
    "dimension_code",
    "dimensionCode",
    "concept",
    "concept_code",
    "conceptCode",
  );
  const label = readString(
    record,
    "display_name",
    "displayName",
    "displayname",
    "label",
  );
  return { code, label };
}

function normalizeWeight(value: unknown): OtoWeight {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 1;
}

function normalizeDimensionItems(raw: unknown): AutoTaggingPlanDimensionItem[] {
  if (!Array.isArray(raw)) return [];
  const items: AutoTaggingPlanDimensionItem[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const item = normalizeAutoTaggingPlanDimensionItem(entry);
    if (!item) continue;
    const key = item.concept.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(item);
  }
  return items;
}

/** Derive cascade subtype codes/names from dimension filter items (or legacy sub_type). */
function deriveSubTypeFromItems(
  items: readonly AutoTaggingPlanDimensionItem[],
  legacySubType = "",
  legacySubTypeName = "",
): { code: string; name: string } {
  if (items.length > 0) {
    const first = items[0]!;
    if (first.all_values) {
      return { code: "__all__", name: "All" };
    }
    return {
      code: first.values.map((value) => value.code).join(","),
      name: first.values
        .map((value) => value.name.trim() || value.code)
        .join(", "),
    };
  }
  const legacy = legacySubType.trim();
  const legacyName = legacySubTypeName.trim();
  return {
    code: legacy,
    name: legacyName || legacy,
  };
}

function hydrateUpdateRecordsFromItems(
  items: readonly AutoTaggingPlanDimensionItem[],
  legacyList: readonly string[],
): { concept: string; values: string[] } {
  if (items.length > 0) {
    const source = items[0]!;
    if (source.all_values) {
      return { concept: source.concept, values: [] };
    }
    return {
      concept: source.concept,
      values: source.values.map((value) => value.name.trim() || value.code),
    };
  }
  return { concept: "", values: [...legacyList] };
}

function normalizeDimension(raw: unknown): OtoMatchCondition | null {
  if (typeof raw === "string") {
    const code = raw.trim();
    if (!code) return null;
    return {
      dimensionCode: code,
      dimensionLabel: displayNameForCode(code),
      weight: 1,
    };
  }
  const record = asRecord(raw);
  if (!record) return null;

  // Prefer nested `dimension: { name, display_name }`; keep string/flat aliases.
  const nestedRef = readDimensionRef(record.dimension);
  let code = nestedRef.code;
  let label = nestedRef.label;

  if (!code) {
    code = readString(
      record,
      "dimension_code",
      "dimensionCode",
      "concept",
      "concept_code",
      "conceptCode",
      "code",
      "name",
    );
  }
  // Legacy: `dimension` was a plain string field (not an object).
  if (!code && typeof record.dimension === "string") {
    code = record.dimension.trim();
  }

  if (!label) {
    label = readString(
      record,
      "dimension_label",
      "dimensionLabel",
      "concept_name",
      "conceptName",
      "label",
      "display_name",
      "displayName",
      "displayname",
    );
  }

  if (!code) return null;

  return {
    dimensionCode: code,
    // List UI shows dimensionLabel only (via formatOtoMatchingDimensions).
    dimensionLabel: displayNameForCode(code, label),
    weight: normalizeWeight(record.weight),
  };
}

function normalizeDimensionAvailability(
  record: Record<string, unknown>,
): OtoDimensionAvailability {
  const availableOn = record.available_on ?? record.present_on ?? record.sides;
  if (Array.isArray(availableOn)) {
    const sides = availableOn
      .map((item) => String(item).trim().toLowerCase())
      .filter(Boolean);
    const hasA = sides.some((side) => side === "a" || side === "object_a");
    const hasB = sides.some((side) => side === "b" || side === "object_b");
    if (hasA && !hasB) return "a_only";
    if (!hasA && hasB) return "b_only";
    if (hasA && hasB) return "both";
  }

  const sideRaw = readString(
    record,
    "side",
    "availability",
    "available_on",
    "present_on",
  ).toLowerCase();
  if (
    sideRaw === "a" ||
    sideRaw === "object_a" ||
    sideRaw === "a_only" ||
    sideRaw === "object_a_only"
  ) {
    return "a_only";
  }
  if (
    sideRaw === "b" ||
    sideRaw === "object_b" ||
    sideRaw === "b_only" ||
    sideRaw === "object_b_only"
  ) {
    return "b_only";
  }
  if (sideRaw === "both" || sideRaw === "ab" || sideRaw === "a_and_b") {
    return "both";
  }

  const onA = readBoolean(
    record,
    "on_a",
    "on_object_a",
    "present_on_a",
    "has_a",
  );
  const onB = readBoolean(
    record,
    "on_b",
    "on_object_b",
    "present_on_b",
    "has_b",
  );
  if (onA === true && onB === false) return "a_only";
  if (onA === false && onB === true) return "b_only";

  const matchable = readBoolean(record, "matchable", "can_match", "is_matchable");
  if (matchable === false) {
    return "a_only";
  }

  return "both";
}

function normalizeDimensionCandidate(raw: unknown): OtoDimensionOption | null {
  if (typeof raw === "string") {
    const code = raw.trim();
    if (!code) return null;
    return {
      code,
      name: displayNameForCode(code),
      availability: "both",
    };
  }
  const normalized = normalizeDimension(raw);
  if (!normalized) return null;
  const record = asRecord(raw);
  return {
    code: normalized.dimensionCode,
    name: normalized.dimensionLabel,
    availability: record ? normalizeDimensionAvailability(record) : "both",
  };
}

function normalizeDimensions(raw: unknown): OtoMatchCondition[] {
  if (!Array.isArray(raw)) return [];
  const out: OtoMatchCondition[] = [];
  for (const item of raw) {
    const normalized = normalizeDimension(item);
    if (normalized) out.push(normalized);
  }
  return out;
}

/**
 * Read object side. Current API only returns `{ type, type_name }`.
 * Legacy `sub_type` / flat `*_object_sub_type` are still accepted for older
 * payloads; prefer `reference_items` / `impacted_items` for filter values.
 */
function normalizeObjectSide(
  nestedRaw: unknown,
  flatRecord: Record<string, unknown>,
  side: "reference" | "impacted",
): {
  type: string;
  typeName: string;
  legacySubType: string;
  legacySubTypeName: string;
} {
  const nested = asRecord(nestedRaw);
  if (nested) {
    const type = readString(nested, "type");
    const legacySubType = readString(nested, "sub_type", "subtype");
    return {
      type,
      typeName: displayNameForCode(type, readString(nested, "type_name", "name")),
      // Legacy only — current responses omit sub_type / sub_type_name.
      legacySubType,
      legacySubTypeName: readString(nested, "sub_type_name", "subtype_name"),
    };
  }

  const legacySide = side === "reference" ? "a" : "b";
  const type = readString(
    flatRecord,
    `${side}_object_type`,
    `object_${legacySide}_type`,
    side === "reference" ? "objectAType" : "objectBType",
  );
  const legacySubType = readString(
    flatRecord,
    `${side}_object_sub_type`,
    `object_${legacySide}_sub_type`,
    side === "reference" ? "objectASubType" : "objectBSubType",
  );
  return {
    type,
    typeName: displayNameForCode(
      type,
      readString(
        flatRecord,
        `${side}_object_type_name`,
        `object_${legacySide}_type_name`,
        side === "reference" ? "objectATypeName" : "objectBTypeName",
      ),
    ),
    legacySubType,
    legacySubTypeName: readString(
      flatRecord,
      `${side}_object_sub_type_name`,
      `object_${legacySide}_sub_type_name`,
      side === "reference" ? "objectASubTypeName" : "objectBSubTypeName",
    ),
  };
}

function normalizeStringList(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of raw) {
      const value =
        typeof item === "string"
          ? item.trim()
          : item && typeof item === "object"
            ? readString(item as Record<string, unknown>, "name", "code", "value")
            : "";
      if (!value || seen.has(value)) continue;
      seen.add(value);
      out.push(value);
    }
    return out;
  }
  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Map API plan payload → UI model.
 * Prefers `reference_object` / `impacted_object` (`type` / `type_name` only);
 * still accepts legacy `object_a` / `object_b` and flat `*_object_type` fields.
 * Filter values come from `reference_items` / `impacted_items`.
 */
export function normalizeOtoPlan(raw: unknown): OtoPlan | null {
  const record = asRecord(raw);
  if (!record) return null;

  const idValue = record.id ?? record.plan_id;
  const id =
    typeof idValue === "number" || typeof idValue === "string"
      ? String(idValue).trim()
      : "";
  if (!id) return null;

  const objectA = normalizeObjectSide(
    record.reference_object ??
      record.referenceObject ??
      record.object_a ??
      record.objectA,
    record,
    "reference",
  );
  const objectB = normalizeObjectSide(
    record.impacted_object ??
      record.impactedObject ??
      record.object_b ??
      record.objectB,
    record,
    "impacted",
  );

  const referenceItems = normalizeDimensionItems(
    record.reference_items ?? record.referenceItems,
  );
  const impactedItems = normalizeDimensionItems(
    record.impacted_items ?? record.impactedItems,
  );
  const assessmentRecordItems = normalizeDimensionItems(
    record.assessment_record_items ?? record.assessmentRecordItems,
  );

  const derivedA = deriveSubTypeFromItems(
    referenceItems,
    objectA.legacySubType,
    objectA.legacySubTypeName,
  );
  const derivedB = deriveSubTypeFromItems(
    impactedItems,
    objectB.legacySubType,
    objectB.legacySubTypeName,
  );

  const dimensions = normalizeDimensions(
    record.dimensions ?? record.matching_dimensions ?? record.matchingDimensions,
  );

  const legacyUpdateRecords = normalizeStringList(
    record.update_assessment_record ??
      record.updateAssessmentRecord ??
      record.update_fields ??
      record.updateFields,
  );
  const updateRecords = hydrateUpdateRecordsFromItems(
    assessmentRecordItems,
    legacyUpdateRecords,
  );

  return {
    id,
    name: readString(record, "name"),
    objectAType: objectA.type,
    objectASubType: derivedA.code,
    objectATypeName: objectA.typeName,
    objectASubTypeName: derivedA.name,
    objectBType: objectB.type,
    objectBSubType: derivedB.code,
    objectBTypeName: objectB.typeName,
    objectBSubTypeName: derivedB.name,
    referenceItems,
    impactedItems,
    matchingDimensions: dimensions,
    assessmentType: readString(
      record,
      "assessment_type",
      "assessmentType",
      "template",
    ),
    prompt: readString(
      record,
      "assessment_prompt",
      "assessmentPrompt",
      "prompt",
    ),
    assessmentRecordItems,
    updateAssessmentRecord: updateRecords.values,
    updateAssessmentRecordConcept: updateRecords.concept,
    enableAssessment: readBoolean(record, "enable_assessment", "enableAssessment") ?? false,
    assessmentReady:
      readBoolean(record, "assessment_ready", "assessmentReady") ?? false,
    assessmentReadyReason: readString(
      record,
      "assessment_ready_reason",
      "assessmentReadyReason",
    ),
    active: readBoolean(record, "active") ?? true,
  };
}

function extractListPayload(data: unknown): {
  items: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(data)) {
    return { items: data };
  }
  const record = asRecord(data);
  if (!record) return { items: [] };

  const nested = asRecord(record.data);
  const itemsCandidate =
    record.items ??
    record.plans ??
    record.results ??
    nested?.items ??
    nested?.plans ??
    nested?.results;

  const items = Array.isArray(itemsCandidate) ? itemsCandidate : [];
  return {
    items,
    total: readNumber(record, "total", "total_count", "totalCount") ??
      (nested ? readNumber(nested, "total", "total_count", "totalCount") : undefined),
    page: readNumber(record, "page") ??
      (nested ? readNumber(nested, "page") : undefined),
    pageSize:
      readNumber(record, "page_size", "pageSize") ??
      (nested ? readNumber(nested, "page_size", "pageSize") : undefined),
  };
}

function mapDimensionItemsForWrite(
  items: readonly AutoTaggingPlanDimensionItemWrite[] | undefined,
): AutoTaggingPlanDimensionItemWrite[] {
  return (items ?? []).map((entry) => toAutoTaggingPlanDimensionItemWrite(entry));
}

function mapMatchDimensionsForWrite(
  items: readonly OtoMatchCondition[],
): Array<{ dimension: string; weight: number }> {
  return items.map((item) => ({
    dimension: item.dimensionCode.trim(),
    weight: Number.isFinite(item.weight) ? item.weight : 1,
  }));
}

function resolveAssessmentTypeForWrite(assessmentType?: string): string {
  const normalized = (assessmentType ?? "").trim().toLowerCase();
  if (normalized === "implementation" || normalized === "tagging") {
    return normalized;
  }
  // OTO default template.
  return "implementation";
}

function toCreateBody(payload: OtoPlanUpsertPayload): Record<string, unknown> {
  const trimmedPrompt = payload.prompt.trim();
  return {
    name: payload.name.trim(),
    // Object sides: type only — no sub_type.
    reference_object_type: payload.objectAType.trim(),
    impacted_object_type: payload.objectBType.trim(),
    reference_items: mapDimensionItemsForWrite(payload.referenceItems),
    impacted_items: mapDimensionItemsForWrite(payload.impactedItems),
    assessment_type: resolveAssessmentTypeForWrite(payload.assessmentType),
    assessment_record_items: mapDimensionItemsForWrite(
      payload.assessmentRecordItems,
    ),
    dimensions: mapMatchDimensionsForWrite(payload.matchingDimensions),
    assessment_prompt: trimmedPrompt || null,
    // Enable assessment whenever a prompt is configured.
    enable_assessment: Boolean(trimmedPrompt),
    active: payload.active ?? true,
  };
}

function toUpdateBody(
  payload: Partial<OtoPlanUpsertPayload> & { active?: boolean },
): Record<string, unknown> {
  // Never send read-only assessment_ready / assessment_ready_reason.
  // Never send object sub_type — filters use reference_items / impacted_items.
  const body: Record<string, unknown> = {};
  if (payload.name !== undefined) {
    body.name = payload.name.trim();
  }
  if (payload.objectAType !== undefined) {
    body.reference_object_type = payload.objectAType.trim();
  }
  if (payload.objectBType !== undefined) {
    body.impacted_object_type = payload.objectBType.trim();
  }
  if (payload.referenceItems !== undefined) {
    body.reference_items = mapDimensionItemsForWrite(payload.referenceItems);
  }
  if (payload.impactedItems !== undefined) {
    body.impacted_items = mapDimensionItemsForWrite(payload.impactedItems);
  }
  if (payload.assessmentType !== undefined) {
    body.assessment_type = resolveAssessmentTypeForWrite(payload.assessmentType);
  }
  if (payload.assessmentRecordItems !== undefined) {
    body.assessment_record_items = mapDimensionItemsForWrite(
      payload.assessmentRecordItems,
    );
  }
  if (payload.matchingDimensions !== undefined) {
    body.dimensions = mapMatchDimensionsForWrite(payload.matchingDimensions);
  }
  if (payload.prompt !== undefined) {
    const trimmedPrompt = payload.prompt.trim();
    // Empty / whitespace → null clears the stored prompt on PATCH.
    body.assessment_prompt = trimmedPrompt || null;
    // Keep enable_assessment in sync with whether a prompt is configured.
    body.enable_assessment = Boolean(trimmedPrompt);
  } else if (payload.enableAssessment !== undefined) {
    body.enable_assessment = payload.enableAssessment;
  }
  if (payload.active !== undefined) {
    body.active = payload.active;
  }
  return body;
}

function assertSuccess(result: {
  status?: string;
  message?: string | null;
}): void {
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Request failed.");
  }
}

/**
 * Assemble list query params from homepage Reference / Impacted filters.
 * Filter value format remains `type|subtype` for UI keys, but only `type` is
 * sent — API no longer accepts object sub_type on list.
 */
export function buildOtoListQueryParams(
  query: OtoPlanListQuery = {},
): Record<string, string | number | boolean> {
  const objectA = parseOtoObjectFilterKey(query.objectA);
  const objectB = parseOtoObjectFilterKey(query.objectB);
  const page = Math.max(1, query.page ?? 1);
  const pageSize = Math.max(1, query.pageSize ?? OTO_PLANS_DEFAULT_PAGE_SIZE);

  const params: Record<string, string | number | boolean> = {
    page,
    page_size: pageSize,
  };

  if (objectA.type) {
    params.reference_object_type = objectA.type;
  }
  if (objectB.type) {
    params.impacted_object_type = objectB.type;
  }

  const keyword = normalizeAutoTaggingPlanNameQuery(query.keyword ?? "");
  if (keyword) {
    params.keyword = keyword;
  }
  const name = normalizeAutoTaggingPlanNameQuery(query.name ?? "");
  if (name) {
    params.name = name;
  }

  if (typeof query.active === "boolean") {
    params.active = query.active;
  }

  return params;
}

export async function fetchOtoPlans(
  query: OtoPlanListQuery = {},
): Promise<OtoPlanListResult> {
  const params = buildOtoListQueryParams(query);
  const page = Number(params.page) || 1;
  const pageSize = Number(params.page_size) || OTO_PLANS_DEFAULT_PAGE_SIZE;

  const result = await apiClient.get<unknown>("/assessment-plans", params);

  assertSuccess(result);

  const extracted = extractListPayload(result.data);
  const items = extracted.items
    .map((item) => normalizeOtoPlan(item))
    .filter((item): item is OtoPlan => item !== null);

  return {
    items,
    total: extracted.total ?? items.length,
    page: extracted.page ?? page,
    pageSize: extracted.pageSize ?? pageSize,
  };
}

export async function fetchOtoPlanById(planId: string): Promise<OtoPlan | null> {
  const id = planId.trim();
  if (!id) return null;

  const result = await apiClient.get<unknown>(
    `/assessment-plans/${encodeURIComponent(id)}`,
  );
  assertSuccess(result);
  return normalizeOtoPlan(result.data);
}

/** Which OTO object-type groups to expose from configurable-concepts. */
export type OtoPlanTypesSide = "all" | "reference" | "impacted";

const assessmentPlanTypesInflight = new Map<
  OtoPlanTypesSide,
  Promise<AutoTaggingPlanTypeOption[]>
>();

function planTypesFromOtoConceptGroups(
  groups: readonly OtoObjectConceptsGroup[],
): AutoTaggingPlanTypeOption[] {
  const byCode = new Map<string, AutoTaggingPlanTypeOption>();
  for (const group of groups) {
    const code = group.code.trim();
    if (!code) continue;
    const key = code.toLowerCase();
    if (byCode.has(key)) continue;
    byCode.set(key, {
      code,
      name: group.name.trim() || code,
      objectSubTypes: [],
      configurable: true,
    });
  }
  return Array.from(byCode.values());
}

/**
 * Build Object type options from the OTO configurable-concepts catalog.
 * `all` unions Reference + Impacted; side-specific lists power each cascade.
 */
export function planTypesFromOtoConfigurableConcepts(
  catalog: OtoConfigurableConcepts,
  side: OtoPlanTypesSide = "all",
): AutoTaggingPlanTypeOption[] {
  if (side === "reference") {
    return planTypesFromOtoConceptGroups(catalog.referenceObjectConcepts);
  }
  if (side === "impacted") {
    return planTypesFromOtoConceptGroups(catalog.impactedObjectConcepts);
  }
  return planTypesFromOtoConceptGroups([
    ...catalog.referenceObjectConcepts,
    ...catalog.impactedObjectConcepts,
  ]);
}

/**
 * Object type options for OTO pickers / filters.
 * Derived from `GET /assessment-plans/configurable-concepts` (types endpoint removed).
 */
export async function fetchAssessmentPlanTypes(
  side: OtoPlanTypesSide = "all",
): Promise<AutoTaggingPlanTypeOption[]> {
  const existing = assessmentPlanTypesInflight.get(side);
  if (existing) return existing;

  const request = (async () => {
    try {
      const catalog = await fetchAssessmentPlanConfigurableConcepts();
      return planTypesFromOtoConfigurableConcepts(catalog, side);
    } catch {
      return [];
    }
  })().finally(() => {
    assessmentPlanTypesInflight.delete(side);
  });

  assessmentPlanTypesInflight.set(side, request);
  return request;
}

/** @internal Test helper — clears the in-flight assessment plan-types request. */
export function __resetAssessmentPlanTypesCacheForTests(): void {
  assessmentPlanTypesInflight.clear();
}

/** GET `/assessment-plans/active` — active plans for the current tenant. */
export async function fetchOtoActivePlansList(): Promise<OtoPlan[]> {
  const result = await apiClient.get<unknown>("/assessment-plans/active");
  assertSuccess(result);

  const extracted = extractListPayload(result.data);
  const rawItems =
    extracted.items.length > 0
      ? extracted.items
      : Array.isArray(result.data)
        ? result.data
        : [];

  return rawItems
    .map((item) => normalizeOtoPlan(item))
    .filter((item): item is OtoPlan => item !== null);
}

const otoDefaultPromptInflight = new Map<string, Promise<string>>();

function normalizeOtoDefaultPromptPayload(payload: unknown): string {
  if (typeof payload === "string") return payload.trim();
  if (!payload || typeof payload !== "object") return "";
  const record = payload as Record<string, unknown>;
  return String(
    record.prompt ??
      record.default_prompt ??
      record.defaultPrompt ??
      record.assessment_prompt ??
      record.assessmentPrompt ??
      "",
  ).trim();
}

export type FetchOtoDefaultPromptParams = {
  referenceObjectType: string;
  impactedObjectType: string;
  /** @deprecated API query is type-only; ignored. */
  referenceObjectSubType?: string;
  /** @deprecated API query is type-only; ignored. */
  impactedObjectSubType?: string;
};

/**
 * GET `/assessment-plans/default-prompt` — preview code-owned default for the
 * directed Reference → Impact type pair (query: types only, no sub_type).
 */
export async function fetchOtoDefaultPrompt(
  params: FetchOtoDefaultPromptParams,
): Promise<string> {
  const referenceObjectType = params.referenceObjectType.trim();
  const impactedObjectType = params.impactedObjectType.trim();
  if (!referenceObjectType || !impactedObjectType) return "";

  const cacheKey = [referenceObjectType, impactedObjectType].join("|");

  const inflight = otoDefaultPromptInflight.get(cacheKey);
  if (inflight) return inflight;

  const request = apiClient
    .get<unknown>("/assessment-plans/default-prompt", {
      reference_object_type: referenceObjectType,
      impacted_object_type: impactedObjectType,
    })
    .then((result) => {
      assertSuccess(result);
      return normalizeOtoDefaultPromptPayload(result.data);
    })
    .finally(() => {
      otoDefaultPromptInflight.delete(cacheKey);
    });

  otoDefaultPromptInflight.set(cacheKey, request);
  return request;
}

export function __resetOtoDefaultPromptCacheForTests(): void {
  otoDefaultPromptInflight.clear();
}

export async function createOtoPlan(
  payload: OtoPlanUpsertPayload,
): Promise<OtoPlan> {
  const result = await apiClient.post<unknown>(
    "/assessment-plans",
    toCreateBody(payload),
  );
  assertSuccess(result);
  const plan = normalizeOtoPlan(result.data);
  if (!plan) {
    throw new Error(result.message?.trim() || "Failed to create plan.");
  }
  invalidateAutoTaggingPlanNamesCache();
  return plan;
}

export async function updateOtoPlan(
  planId: string,
  payload: OtoPlanUpsertPayload,
): Promise<OtoPlan> {
  const result = await apiClient.patch<unknown>(
    `/assessment-plans/${encodeURIComponent(planId)}`,
    toUpdateBody(payload),
  );
  assertSuccess(result);
  const plan = normalizeOtoPlan(result.data);
  if (!plan) {
    throw new Error(result.message?.trim() || "Failed to update plan.");
  }
  invalidateAutoTaggingPlanNamesCache();
  return plan;
}

export async function updateOtoPlanActive(
  planId: string,
  active: boolean,
): Promise<OtoPlan> {
  const result = await apiClient.patch<unknown>(
    `/assessment-plans/${encodeURIComponent(planId)}`,
    { active },
  );
  assertSuccess(result);
  const plan = normalizeOtoPlan(result.data);
  if (!plan) {
    throw new Error(result.message?.trim() || "Failed to update plan status.");
  }
  return plan;
}

export async function deleteOtoPlan(planId: string): Promise<void> {
  const result = await apiClient.delete<unknown>(
    `/assessment-plans/${encodeURIComponent(planId)}`,
  );
  assertSuccess(result);
  invalidateAutoTaggingPlanNamesCache();
}

/**
 * Dimension candidates for a type pair
 * (`GET /assessment-plans/dimensions` — query: types only, no sub_type).
 */
export async function fetchOtoDimensionCandidates(
  objectAType: string,
  objectBType: string,
): Promise<OtoDimensionOption[]> {
  const a = objectAType.trim();
  const b = objectBType.trim();
  if (!a || !b) return [];

  const result = await apiClient.get<unknown>("/assessment-plans/dimensions", {
    reference_object_type: a,
    impacted_object_type: b,
  });
  assertSuccess(result);

  const data = result.data;
  let rawList: unknown[] = [];
  if (Array.isArray(data)) {
    rawList = data;
  } else {
    const record = asRecord(data);
    const nested = asRecord(record?.data);
    const candidate =
      record?.dimensions ??
      record?.items ??
      record?.candidates ??
      record?.results ??
      nested?.dimensions ??
      nested?.items ??
      nested?.candidates ??
      nested?.results ??
      record?.data;
    if (Array.isArray(candidate)) rawList = candidate;
  }

  const options: OtoDimensionOption[] = [];
  const seen = new Set<string>();
  for (const item of rawList) {
    const normalized = normalizeDimensionCandidate(item);
    if (!normalized || seen.has(normalized.code)) continue;
    seen.add(normalized.code);
    options.push(normalized);
  }
  return options;
}

export type OtoMatchConditionDraft = {
  id: string;
  dimensionCode: string;
  dimensionLabel: string;
  /** Weight field text — 0–1 inclusive, at most one decimal place. */
  weight: string;
};

export function createEmptyMatchConditionDraft(): OtoMatchConditionDraft {
  return {
    id: `cond-${crypto.randomUUID()}`,
    dimensionCode: "",
    dimensionLabel: "",
    weight: "",
  };
}

export type OtoMatchConditionsValidation = {
  valid: boolean;
  formError?: string;
  rowErrors: Record<string, { dimension?: string; weight?: string }>;
};

export function validateOtoMatchConditions(
  rows: readonly OtoMatchConditionDraft[],
  options?: { maxDimensions?: number | null },
): OtoMatchConditionsValidation {
  if (rows.length === 0) {
    return {
      valid: false,
      formError: "Add at least one match condition.",
      rowErrors: {},
    };
  }

  const maxDimensions = options?.maxDimensions;
  if (
    typeof maxDimensions === "number" &&
    maxDimensions > 0 &&
    rows.length > maxDimensions
  ) {
    return {
      valid: false,
      formError: `You can add at most ${maxDimensions} dimensions.`,
      rowErrors: {},
    };
  }

  const rowErrors: OtoMatchConditionsValidation["rowErrors"] = {};
  const seen = new Map<string, string>();

  for (const row of rows) {
    const errors: { dimension?: string; weight?: string } = {};
    if (!row.dimensionCode.trim()) {
      errors.dimension = "Please select a dimension.";
    } else {
      const key = row.dimensionCode.trim().toLowerCase();
      const previousId = seen.get(key);
      if (previousId) {
        errors.dimension = "Dimension must be unique.";
        const prev = rowErrors[previousId] ?? {};
        rowErrors[previousId] = {
          ...prev,
          dimension: prev.dimension || "Dimension must be unique.",
        };
      } else {
        seen.set(key, row.id);
      }
    }
    const weightError = otoWeightInputError(row.weight);
    if (weightError) {
      errors.weight = weightError;
    }
    if (errors.dimension || errors.weight) {
      rowErrors[row.id] = errors;
    }
  }

  return {
    valid: Object.keys(rowErrors).length === 0,
    rowErrors,
  };
}

export function toOtoMatchConditions(
  rows: readonly OtoMatchConditionDraft[],
): OtoMatchCondition[] {
  return rows.map((row) => ({
    dimensionCode: row.dimensionCode.trim(),
    dimensionLabel: row.dimensionLabel.trim() || row.dimensionCode.trim(),
    weight: parseOtoWeightInput(row.weight),
  }));
}

export type OtoObjectConceptsGroup = {
  code: string;
  name: string;
  concepts: ConfigurableConcept[];
};

export type OtoConfigurableConcepts = {
  referenceObjectConcepts: OtoObjectConceptsGroup[];
  impactedObjectConcepts: OtoObjectConceptsGroup[];
  assessmentRecordConcepts: ConfigurableConcept[];
};

function normalizeOtoConceptGroup(raw: unknown): OtoObjectConceptsGroup | null {
  const record = asRecord(raw);
  if (!record) return null;
  const code = readString(record, "code", "type", "target_type", "targetType");
  if (!code) return null;
  const name = readString(record, "name", "type_name", "typeName") || code;
  const conceptsRaw =
    record.concepts ?? record.concept_list ?? record.conceptList ?? [];
  const concepts = Array.isArray(conceptsRaw)
    ? conceptsRaw
        .map((item) => normalizeConfigurableConcept(item))
        .filter((item): item is ConfigurableConcept => item !== null)
    : [];
  return { code, name, concepts };
}

function normalizeOtoConceptGroups(raw: unknown): OtoObjectConceptsGroup[] {
  if (!Array.isArray(raw)) return [];
  const groups: OtoObjectConceptsGroup[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const group = normalizeOtoConceptGroup(entry);
    if (!group) continue;
    const key = group.code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    groups.push(group);
  }
  return groups;
}

function normalizeOtoConceptList(raw: unknown): ConfigurableConcept[] {
  if (!Array.isArray(raw)) return [];
  const looksGrouped = raw.some((item) => {
    const record = asRecord(item);
    return Boolean(
      record && (record.concepts || record.concept_list || record.conceptList),
    );
  });
  if (looksGrouped) {
    const seen = new Set<string>();
    const concepts: ConfigurableConcept[] = [];
    for (const group of normalizeOtoConceptGroups(raw)) {
      for (const concept of group.concepts) {
        const key = concept.code.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        concepts.push(concept);
      }
    }
    return concepts;
  }
  return raw
    .map((item) => normalizeConfigurableConcept(item))
    .filter((item): item is ConfigurableConcept => item !== null);
}

export function parseOtoConfigurableConceptsPayload(
  payload: unknown,
): OtoConfigurableConcepts {
  const record = asRecord(payload);
  if (!record) {
    return {
      referenceObjectConcepts: [],
      impactedObjectConcepts: [],
      assessmentRecordConcepts: [],
    };
  }
  const nested = asRecord(record.data) ?? record;
  return {
    referenceObjectConcepts: normalizeOtoConceptGroups(
      nested.reference_object_concepts ?? nested.referenceObjectConcepts,
    ),
    impactedObjectConcepts: normalizeOtoConceptGroups(
      nested.impacted_object_concepts ?? nested.impactedObjectConcepts,
    ),
    assessmentRecordConcepts: normalizeOtoConceptList(
      nested.assessment_record_concepts ?? nested.assessmentRecordConcepts,
    ),
  };
}

let otoConfigurableConceptsInflight: Promise<OtoConfigurableConcepts> | null =
  null;
let otoConfigurableConceptsCache: OtoConfigurableConcepts | null = null;

/**
 * GET `/assessment-plans/configurable-concepts` — bundled Reference / Impacted /
 * assessment-record concept catalogs for OTO cascades and Update Assessment Record.
 */
export async function fetchAssessmentPlanConfigurableConcepts(): Promise<OtoConfigurableConcepts> {
  if (otoConfigurableConceptsCache) return otoConfigurableConceptsCache;
  if (otoConfigurableConceptsInflight) return otoConfigurableConceptsInflight;

  otoConfigurableConceptsInflight = (async () => {
    const result = await apiClient.get<unknown>(
      "/assessment-plans/configurable-concepts",
    );
    assertSuccess(result);
    const parsed = parseOtoConfigurableConceptsPayload(result.data ?? result);
    otoConfigurableConceptsCache = parsed;
    return parsed;
  })().finally(() => {
    otoConfigurableConceptsInflight = null;
  });

  return otoConfigurableConceptsInflight;
}

/** Concepts for one object type from the OTO configurable-concepts catalog. */
export async function fetchOtoSideConfigurableConcepts(
  side: "reference" | "impacted",
  targetType: string,
): Promise<ConfigurableConcept[]> {
  const typeCode = targetType.trim();
  if (!typeCode) return [];
  const catalog = await fetchAssessmentPlanConfigurableConcepts();
  const groups =
    side === "reference"
      ? catalog.referenceObjectConcepts
      : catalog.impactedObjectConcepts;
  const match = groups.find(
    (group) => group.code.toLowerCase() === typeCode.toLowerCase(),
  );
  return match?.concepts ?? [];
}

/** Assessment-record concepts from the OTO configurable-concepts catalog. */
export async function fetchOtoAssessmentRecordConcepts(
  _targetType?: string | null,
): Promise<ConfigurableConcept[]> {
  const catalog = await fetchAssessmentPlanConfigurableConcepts();
  return catalog.assessmentRecordConcepts;
}

/** @internal Test helper — clears OTO configurable-concepts cache. */
export function __resetAssessmentPlanConfigurableConceptsCacheForTests(): void {
  otoConfigurableConceptsInflight = null;
  otoConfigurableConceptsCache = null;
}
